import { useEffect, useState } from 'react'

/**
 * Radix implements a modal layer (Dialog, AlertDialog, modal Popover…) by
 * writing `pointer-events: none` onto `<body>` and re-enabling pointer
 * events only inside its own portal. Anything `fixed` OUTSIDE that portal
 * — our top status banners, for instance — keeps every pixel of its visual
 * prominence while becoming completely unclickable.
 *
 * Measured 2026-08-26 against a modal `<Dialog>`: the slow-connection
 * banner's 148px "switch to offline mode" button rendered at (1168, 16),
 * fully opaque, with a computed `pointer-events: none`; `elementFromPoint`
 * at its centre returned `<html>`. A call to action that cannot be called
 * is worse than no call at all, so surfaces living outside the modal read
 * this and either hide their controls or step aside entirely.
 *
 * The predicate is exported on its own so it can be pinned in jsdom
 * without mounting React.
 */
export function isPointerInputBlocked(doc: Document = document): boolean {
  return doc.body.style.pointerEvents === 'none'
}

/**
 * Whether any Radix layer still needs the `body` pointer-events guard.
 *
 * The first version of this asked "is a dialog or alertdialog open?" and that
 * was the wrong question: the guard is not set by dialogs, it is set by Radix's
 * `DismissableLayer` for ANY layer mounted with `disableOutsidePointerEvents`.
 * `Select` is one of those (`@radix-ui/react-select` passes the flag) and it
 * renders `role="listbox"`, so a role list misses it — and closing a dialog and
 * opening a select inside the 500ms release window would have handed pointer
 * input back to the page underneath a live listbox.
 *
 * So ask about the MECHANISM instead. While any layer disables outside pointer
 * events, `DismissableLayer` writes an inline `pointer-events: auto` onto every
 * live layer node (`isBodyPointerEventsDisabled ? … "auto" : undefined` in
 * `@radix-ui/react-dismissable-layer`). That inline value existing anywhere in
 * the document IS the statement "some layer still wants the guard", whatever
 * role it happens to carry.
 *
 * The role check is kept alongside it, not because it adds coverage today but
 * because this app replaced Radix's Dialog Overlay with a plain div (for an
 * iOS text-selection bug), and a second, independent signal costs nothing here.
 */
export function hasLayerNeedingPointerEventsGuard(doc: Document = document): boolean {
  const openModal = doc.querySelector(
    '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
  )
  if (openModal !== null) return true

  for (const element of doc.querySelectorAll<HTMLElement>('[style]')) {
    // `body` and `html` are what the guard is applied TO, never layers. They
    // have to be skipped explicitly because at least one place in this app sets
    // `document.body.style.pointerEvents = 'auto'` by hand
    // (`NewIssueDialogLocation.tsx`, freeing the full-screen map from the Radix
    // guard). Without this, one click on that button would leave the predicate
    // permanently true and the release below would never run again — the fix
    // would have moved the defect rather than closed it.
    if (element === doc.body || element === doc.documentElement) continue
    if (element.style.pointerEvents === 'auto') return true
  }

  return false
}

export function useIsInteractionBlocked(): boolean {
  const [blocked, setBlocked] = useState(() =>
    typeof document === 'undefined' ? false : isPointerInputBlocked(),
  )

  useEffect(() => {
    const read = () => setBlocked(isPointerInputBlocked())
    read()

    // `style` is the only attribute that carries the signal — Radix writes
    // the inline property and removes it on close. Watching the whole body
    // subtree would fire on every render inside the app.
    const observer = new MutationObserver(read)
    observer.observe(document.body, { attributes: true, attributeFilter: ['style'] })

    return () => observer.disconnect()
  }, [])

  return blocked
}
